import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  CheckCircle, 
  Clock, 
  User, 
  RotateCcw,
  Play,
  Settings,
  Eye,
  EyeOff,
  Plus,
  Trash2
} from 'lucide-react';

const Miscounts = () => {
  const { user } = useContext(AuthContext);
  const [aisles, setAisles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [stores, setStores] = useState([]);
  const [settings, setSettings] = useState({ 
    max_rounds: 3,
    round_2_enabled: true, 
    round_3_enabled: true 
  });
  const [selectedRound, setSelectedRound] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignDialog, setAssignDialog] = useState({ open: false, aisle: null });
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [manualNameMode, setManualNameMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [newAisleName, setNewAisleName] = useState('');
  const [newAisleStoreId, setNewAisleStoreId] = useState('');
  const [addingAisle, setAddingAisle] = useState(false);
  const token = localStorage.getItem('token');

  // Get the current store ID from localStorage (set by store dropdown)
  const getSelectedStoreId = () => {
    return localStorage.getItem('selectedStoreId') || user?.store_id;
  };

  // Permission checks
  const isNetworkAdmin = user?.role === 'network_admin';
  const canToggleRounds = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager', 'stocktake_controller', 'stocktake_champion'].includes(user?.role);
  const canAddRound = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager'].includes(user?.role);
  const canDeleteRound = isNetworkAdmin;
  const canManageAisles = ['network_admin', 'store_manager', 'stocktake_controller'].includes(user?.role);
  const canResetRounds = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager', 'stocktake_controller'].includes(user?.role);
  const canMarkComplete = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager', 'stocktake_controller', 'stocktake_champion'].includes(user?.role);
  const canManualAssign = ['network_admin', 'regional_manager', 'complex_manager'].includes(user?.role);

  useEffect(() => {
    fetchData();
    // Fetch stores for Network Admin
    if (user?.role === 'network_admin') {
      fetchStores();
    }
    const interval = setInterval(fetchData, 30000);
    
    // Listen for store changes
    const handleStoreChange = () => {
      fetchData();
    };
    window.addEventListener('storeChanged', handleStoreChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storeChanged', handleStoreChange);
    };
  }, [user?.role]);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${API}/stores`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    }
  };

  const fetchData = async () => {
    try {
      const storeId = getSelectedStoreId();
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      
      const [aisleRes, assignRes, rosterRes, settingsRes] = await Promise.all([
        fetch(`${API}/miscount-aisles${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/miscount-assignments${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/rosters${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/miscount-settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (aisleRes.ok) setAisles(await aisleRes.json());
      if (assignRes.ok) setAssignments(await assignRes.json());
      if (rosterRes.ok) setRosters(await rosterRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
    } catch (error) {
      console.error(error);
    }
  };

  // Get Monday & Tuesday rostered team members (Miscounts use Mon/Tue)
  const miscountRosters = rosters.filter(r => r.day === 'Monday' || r.day === 'Tuesday');

  // Get stats for a round
  const getRoundStats = (roundNumber) => {
    const activeAisles = aisles.filter(a => a.is_active);
    const roundAssignments = assignments.filter(a => a.round_number === roundNumber);
    
    const assignedAisleIds = roundAssignments.map(a => a.aisle_id);
    const available = activeAisles.filter(a => !assignedAisleIds.includes(a.id)).length;
    const toBeKeyed = roundAssignments.filter(a => a.status === 'taken').length;
    const complete = roundAssignments.filter(a => a.status === 'complete').length;
    
    const total = activeAisles.length;
    const percentage = total > 0 ? Math.round((complete / total) * 100) : 0;

    return { available, toBeKeyed, complete, total, percentage };
  };

  // Get assignment for an aisle in current round
  const getAssignment = (aisleId) => {
    return assignments.find(a => a.round_number === selectedRound && a.aisle_id === aisleId);
  };

  // Calculate elapsed time
  const getElapsedTime = (startedAt) => {
    if (!startedAt) return '00:00';
    const start = new Date(startedAt);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Assign aisle to team member
  const handleAssign = async () => {
    if (manualNameMode) {
      if (!manualName.trim()) {
        toast.error('Please enter a name');
        return;
      }
    } else {
      if (!selectedAssignee) {
        toast.error('Please select a team member');
        return;
      }
    }

    const roster = !manualNameMode ? miscountRosters.find(r => r.id === selectedAssignee) : null;
    const assigneeName = manualNameMode ? manualName.trim() : (roster?.user_name || 'Unknown');
    const assigneeId = manualNameMode ? `manual_${Date.now()}` : (roster?.user_id || selectedAssignee);
    
    try {
      const response = await fetch(`${API}/miscount-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          round_number: selectedRound,
          aisle_id: assignDialog.aisle.id,
          assigned_to: assigneeId,
          assigned_to_name: assigneeName
        })
      });

      if (response.ok) {
        toast.success(`Assigned to ${assigneeName}`);
        setAssignDialog({ open: false, aisle: null });
        setSelectedAssignee('');
        setManualName('');
        setManualNameMode(false);
        fetchData();
      } else {
        toast.error('Failed to assign');
      }
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  // Mark assignment as complete
  const markComplete = async (assignmentId) => {
    try {
      await fetch(`${API}/miscount-assignments/${assignmentId}/complete`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as complete');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Reset a round
  const resetRound = async () => {
    if (!window.confirm(`Are you sure you want to reset Round ${selectedRound}? All assignments will be cleared.`)) {
      return;
    }

    try {
      await fetch(`${API}/miscount-assignments/reset/${selectedRound}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Round ${selectedRound} has been reset`);
      fetchData();
    } catch (error) {
      toast.error('Failed to reset round');
    }
  };

  // Toggle a round on/off
  const toggleRound = async (roundNumber, enabled) => {
    try {
      await fetch(`${API}/miscount-settings`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ [`round_${roundNumber}_enabled`]: enabled })
      });
      setSettings({ ...settings, [`round_${roundNumber}_enabled`]: enabled });
      toast.success(`Round ${roundNumber} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Add a new round
  const addNewRound = async () => {
    const newMaxRounds = (settings.max_rounds || 3) + 1;
    try {
      await fetch(`${API}/miscount-settings`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          max_rounds: newMaxRounds,
          [`round_${newMaxRounds}_enabled`]: true 
        })
      });
      setSettings({ 
        ...settings, 
        max_rounds: newMaxRounds,
        [`round_${newMaxRounds}_enabled`]: true 
      });
      toast.success(`Round ${newMaxRounds} added`);
    } catch (error) {
      toast.error('Failed to add round');
    }
  };

  // Add new aisle
  const handleAddAisle = async () => {
    if (!newAisleName.trim()) {
      toast.error('Please enter an aisle name');
      return;
    }
    
    // Network Admin must select a store
    if (isNetworkAdmin && !newAisleStoreId) {
      toast.error('Please select a store for the aisle');
      return;
    }
    
    setAddingAisle(true);
    try {
      const payload = { name: newAisleName.trim() };
      // Only include store_id if Network Admin selected one
      if (isNetworkAdmin && newAisleStoreId) {
        payload.store_id = newAisleStoreId;
      }
      
      const response = await fetch(`${API}/miscount-aisles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const storeName = isNetworkAdmin ? stores.find(s => s.id === newAisleStoreId)?.name : '';
        toast.success(`Aisle "${newAisleName}" added${storeName ? ` to ${storeName}` : ''}`);
        setNewAisleName('');
        setNewAisleStoreId('');
        fetchData();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || `Failed to add aisle (${response.status})`);
      }
    } catch (error) {
      console.error('Add aisle error:', error);
      toast.error('Network error - please check your connection');
    } finally {
      setAddingAisle(false);
    }
  };

  // Delete a round (Network Admin only)
  const deleteRound = async (roundNumber) => {
    if (!window.confirm(`Are you sure you want to delete Round ${roundNumber}? This will remove the round and all its assignments.`)) {
      return;
    }
    
    try {
      // First reset the round to clear assignments
      await fetch(`${API}/miscount-assignments/reset/${roundNumber}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then update settings to reduce max_rounds and disable the round
      const newMaxRounds = Math.max((settings.max_rounds || 3) - 1, 1);
      await fetch(`${API}/miscount-settings`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          max_rounds: newMaxRounds,
          [`round_${roundNumber}_enabled`]: false 
        })
      });
      
      setSettings(prev => ({ 
        ...prev, 
        max_rounds: newMaxRounds,
        [`round_${roundNumber}_enabled`]: false 
      }));
      toast.success(`Round ${roundNumber} deleted`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete round');
    }
  };

  // Toggle aisle active status
  const toggleAisle = async (aisleId, isActive) => {
    try {
      await fetch(`${API}/miscount-aisles/${aisleId}?is_active=${isActive}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success('Aisle updated');
    } catch (error) {
      toast.error('Failed to update aisle');
    }
  };

  // Delete aisle
  const handleDeleteAisle = async (aisleId, aisleName) => {
    if (!window.confirm(`Are you sure you want to delete "${aisleName}"? This will also remove all assignments for this aisle.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/miscount-aisles/${aisleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`Aisle "${aisleName}" deleted`);
        fetchData();
      } else {
        toast.error('Failed to delete aisle');
      }
    } catch (error) {
      toast.error('Failed to delete aisle');
    }
  };

  // Get filtered aisles
  const getFilteredAisles = () => {
    return aisles
      .filter(a => a.is_active)
      .filter(a => a.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => a.sort_order - b.sort_order);
  };

  // Get enabled rounds based on settings
  const getEnabledRounds = () => {
    const maxRounds = settings.max_rounds || 3;
    const rounds = [];
    for (let i = 1; i <= maxRounds; i++) {
      if (i === 1 || settings[`round_${i}_enabled`] !== false) {
        rounds.push(i);
      }
    }
    return rounds;
  };

  // Render Round Overview (main page)
  if (!selectedRound) {
    const totalStats = { available: 0, toBeKeyed: 0, complete: 0 };
    const rounds = getEnabledRounds();
    
    rounds.forEach(r => {
      const stats = getRoundStats(r);
      totalStats.available += stats.available;
      totalStats.toBeKeyed += stats.toBeKeyed;
      totalStats.complete += stats.complete;
    });

    const hasNoAisles = aisles.filter(a => a.is_active).length === 0;

    return (
      <div className="space-y-8" data-testid="miscounts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Miscounts</h1>
            <p className="text-slate-600">Monday/Tuesday miscount resolution by aisle</p>
          </div>
          <div className="flex gap-2">
            {canAddRound && (
              <Button 
                variant="outline" 
                onClick={addNewRound}
                data-testid="add-round-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Round
              </Button>
            )}
            {(canToggleRounds || canManageAisles) && (
              <Button 
                variant="outline" 
                onClick={() => setSettingsDialogOpen(true)}
                data-testid="miscount-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            )}
          </div>
        </div>

        {/* Warning if no aisles configured */}
        {hasNoAisles && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">!</div>
            <div>
              <p className="font-semibold text-amber-900">No Aisles Configured</p>
              <p className="text-sm text-amber-700 mt-1">
                Click the <strong>Settings</strong> button above to add aisles for miscount tracking.
                {canManageAisles && ' Stocktake Controllers and above can add aisles.'}
              </p>
            </div>
          </div>
        )}

        {/* Round Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {rounds.map(roundNum => {
            const stats = getRoundStats(roundNum);
            return (
              <div
                key={roundNum}
                className={`bg-white rounded-xl border-2 border-slate-200 p-6 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer ${hasNoAisles ? 'opacity-50' : ''}`}
                onClick={() => !hasNoAisles && setSelectedRound(roundNum)}
                data-testid={`miscount-round-${roundNum}-card`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-slate-900">Round {roundNum}</h2>
                  <Badge className={stats.percentage === 100 ? 'bg-green-500' : 'bg-slate-200 text-slate-700'}>
                    {stats.percentage}%
                  </Badge>
                </div>
                <div className="text-lg text-slate-600 mb-4">
                  {stats.complete}/{stats.total} complete
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${stats.percentage === 100 ? 'bg-green-500' : 'bg-orange-500'}`}
                    style={{ width: `${stats.percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Stats */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-3xl font-bold text-slate-600">{totalStats.available}</p>
              <p className="text-sm text-slate-500">Available</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">{totalStats.toBeKeyed}</p>
              <p className="text-sm text-slate-500">To Be Keyed</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{totalStats.complete}</p>
              <p className="text-sm text-slate-500">Complete</p>
            </div>
          </div>
        </div>

        {/* Settings Dialog */}
        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Miscount Settings</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Round Toggles */}
              {canToggleRounds && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Enable/Disable Rounds</h4>
                  <p className="text-sm text-slate-500 mb-4">Toggle rounds on or off. Round 1 is always enabled.{canDeleteRound && ' Network Admin can delete rounds.'}</p>
                  <div className="space-y-3">
                    {Array.from({ length: settings.max_rounds || 3 }, (_, i) => i + 1).map(roundNum => (
                      <div key={roundNum} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <Label className="text-base font-semibold">Round {roundNum}</Label>
                          <p className="text-sm text-slate-500">
                            {roundNum === 1 ? 'Initial miscount check (always on)' : `Follow-up miscount round ${roundNum}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={roundNum === 1 ? true : settings[`round_${roundNum}_enabled`] !== false}
                            onCheckedChange={(enabled) => toggleRound(roundNum, enabled)}
                            disabled={roundNum === 1}
                            data-testid={`miscount-round-${roundNum}-toggle`}
                          />
                          {canDeleteRound && roundNum > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteRound(roundNum)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                              title={`Delete Round ${roundNum}`}
                              data-testid={`delete-round-${roundNum}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Aisle Management */}
              {canManageAisles && (
                <>
                  {/* Add New Aisle */}
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h4 className="font-semibold text-orange-900 mb-3">Add New Aisle</h4>
                    
                    {/* Store selector for Network Admin */}
                    {isNetworkAdmin && (
                      <div className="mb-3">
                        <Label className="text-sm text-orange-800 mb-1 block">Select Store</Label>
                        <Select value={newAisleStoreId} onValueChange={setNewAisleStoreId}>
                          <SelectTrigger className="bg-white" data-testid="aisle-store-select">
                            <SelectValue placeholder="Select store for this aisle..." />
                          </SelectTrigger>
                          <SelectContent>
                            {stores.map(store => (
                              <SelectItem key={store.id} value={store.id}>
                                {store.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter aisle name (e.g., Aisle 1, Aisle 2)..."
                        value={newAisleName}
                        onChange={(e) => setNewAisleName(e.target.value)}
                        className="flex-1"
                        data-testid="miscount-new-aisle-name"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddAisle()}
                      />
                      <Button 
                        onClick={handleAddAisle}
                        disabled={addingAisle || !newAisleName.trim() || (isNetworkAdmin && !newAisleStoreId)}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="miscount-add-aisle-btn"
                      >
                        {addingAisle ? 'Adding...' : 'Add'}
                      </Button>
                    </div>
                    
                    {!isNetworkAdmin && (
                      <p className="text-xs text-orange-700 mt-2">
                        Aisles will be added to your assigned store.
                      </p>
                    )}
                  </div>

                  {/* Aisle List */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Manage Aisles</h4>
                    <p className="text-sm text-slate-500 mb-4">
                      Toggle aisles on/off or delete them. Active aisles appear in miscount rounds.
                    </p>
                    
                    {aisles.length === 0 ? (
                      <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                        <p>No aisles configured yet.</p>
                        <p className="text-sm mt-1">Add your first aisle above to get started.</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {aisles.sort((a, b) => a.sort_order - b.sort_order).map(aisle => (
                          <div 
                            key={aisle.id} 
                            className={`flex items-center justify-between p-3 rounded-lg border ${aisle.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'}`}
                          >
                            <span className={aisle.is_active ? 'text-slate-900 font-medium' : 'text-slate-400'}>
                              {aisle.name}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleAisle(aisle.id, !aisle.is_active)}
                                className="p-1.5 hover:bg-slate-100 rounded"
                                title={aisle.is_active ? 'Disable aisle' : 'Enable aisle'}
                                data-testid={`toggle-aisle-${aisle.id}`}
                              >
                                {aisle.is_active ? (
                                  <Eye className="w-4 h-4 text-green-600" />
                                ) : (
                                  <EyeOff className="w-4 h-4 text-slate-400" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteAisle(aisle.id, aisle.name)}
                                className="p-1.5 hover:bg-red-50 rounded text-red-500 hover:text-red-700"
                                title="Delete aisle"
                                data-testid={`delete-aisle-${aisle.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Info for non-managers */}
              {!canManageAisles && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    Stocktake Controllers and above can manage aisles from this settings panel.
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Render Round Detail Page
  const stats = getRoundStats(selectedRound);
  const filteredAisles = getFilteredAisles();

  return (
    <div className="space-y-6" data-testid="miscount-round-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedRound(null)}
            data-testid="miscount-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">ROUND {selectedRound}</h1>
            <p className="text-slate-500">{stats.complete}/{stats.total} complete - {stats.percentage}%</p>
          </div>
        </div>
        {canResetRounds && (
          <Button 
            variant="outline" 
            onClick={resetRound}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="miscount-reset-round-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Round
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search aisle..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="miscount-search-input"
        />
      </div>

      {/* Aisle List */}
      <div className="space-y-3">
        {filteredAisles.length === 0 ? (
          <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg">
            <p className="text-lg">No aisles found</p>
            <p className="text-sm mt-1">
              {searchQuery ? 'Try a different search term' : 'Add aisles from the Settings page'}
            </p>
          </div>
        ) : (
          filteredAisles.map(aisle => {
            const assignment = getAssignment(aisle.id);
            const isAssigned = assignment && assignment.status === 'taken';
            const isComplete = assignment?.status === 'complete';

            return (
              <div 
                key={aisle.id}
                className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                  isComplete 
                    ? 'bg-green-50 border-green-200' 
                    : isAssigned 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-white border-slate-200 hover:border-orange-300'
                }`}
                data-testid={`miscount-aisle-${aisle.id}`}
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 text-lg">{aisle.name}</h4>
                  {isAssigned && !isComplete ? (
                    <div className="flex items-center gap-2 mt-1">
                      <User className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-700">{assignment.assigned_to_name}</span>
                    </div>
                  ) : isComplete ? (
                    <div className="flex items-center gap-2 mt-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">{assignment.assigned_to_name}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-slate-500">Ready to assign</span>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {isComplete ? (
                    <Badge className="bg-green-500 text-white px-3 py-1">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Complete
                    </Badge>
                  ) : isAssigned ? (
                    <div className="flex items-center gap-3">
                      <Badge className="bg-amber-500 text-white px-3 py-1">
                        <Clock className="w-3 h-3 mr-1" />
                        In Progress
                      </Badge>
                      <span className="text-sm font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded">
                        {getElapsedTime(assignment.started_at)}
                      </span>
                      {canMarkComplete && (
                        <Button
                          size="sm"
                          onClick={() => markComplete(assignment.id)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`miscount-complete-${aisle.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      onClick={() => setAssignDialog({ open: true, aisle })}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid={`miscount-assign-${aisle.id}`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Assign
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => {
        setAssignDialog({ ...assignDialog, open });
        if (!open) {
          setManualNameMode(false);
          setManualName('');
          setSelectedAssignee('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign {assignDialog.aisle?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Toggle between roster selection and manual entry */}
            {canManualAssign && (
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Switch
                  checked={manualNameMode}
                  onCheckedChange={(checked) => {
                    setManualNameMode(checked);
                    setSelectedAssignee('');
                    setManualName('');
                  }}
                  data-testid="manual-name-toggle"
                />
                <Label className="text-sm">
                  {manualNameMode ? 'Manual Entry Mode' : 'Select from Roster'}
                </Label>
                {manualNameMode && (
                  <span className="text-xs text-amber-600 ml-auto">Person not on attendance</span>
                )}
              </div>
            )}

            {manualNameMode ? (
              <div>
                <Label className="mb-2 block">Enter Team Member Name</Label>
                <Input
                  placeholder="Enter name manually..."
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  data-testid="manual-name-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Use this for team members not on the attendance roster
                </p>
              </div>
            ) : (
              <div>
                <Label className="mb-2 block">Select Team Member (Monday/Tuesday Roster)</Label>
                {miscountRosters.length === 0 ? (
                  <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg">
                    <p>No team members rostered for Monday or Tuesday.</p>
                    {canManualAssign && (
                      <p className="mt-2 text-orange-600">Toggle "Manual Entry Mode" above to enter a name manually.</p>
                    )}
                  </div>
                ) : (
                  <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                    <SelectTrigger data-testid="miscount-assignee-select">
                      <SelectValue placeholder="Select team member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {miscountRosters.map(roster => (
                        <SelectItem key={roster.id} value={roster.id}>
                          {roster.user_name} {roster.team_member_number && `(#${roster.team_member_number})`} ({roster.day})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setAssignDialog({ open: false, aisle: null })}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssign} 
                className="bg-orange-600 hover:bg-orange-700"
                disabled={manualNameMode ? !manualName.trim() : (!selectedAssignee && miscountRosters.length > 0)}
                data-testid="miscount-confirm-assign-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Timer & Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Miscounts;
